import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { createResource } from '@/lib/api/resource';
import { type PresignContext, type PresignResult, requestProductPresign } from '@/lib/upload';

import { toListQuery } from './filters';
import {
  deviceListResponseSchema,
  deviceSchema,
  metaCategorySchema,
  metaComponentSchema,
} from './schemas';
import type {
  CreateDeviceBody,
  Device,
  DeviceListResponse,
  DeviceStatus,
  MetaCategory,
  MetaComponent,
} from './types';

const base = endpoints.products.devices;

// Filter draft → backend query params lives in ./filters (derived from the device FilterSpec),
// shared by `listDevices` and the export request so list and export filters cannot drift.
export { toListQuery };

/** CRUD verbs + key factory + query hooks for the Devices catalog resource. Devices' writes
 *  parse and return the entity (writeSchema present); no detail-cache seeding on update. */
export const devicesResource = createResource<CreateDeviceBody, CreateDeviceBody, DeviceStatus>()({
  endpoint: base,
  keyPrefix: 'devices',
  detailSchema: deviceSchema,
  listResponseSchema: deviceListResponseSchema,
  toQuery: toListQuery,
  writeSchema: deviceSchema,
});

export const listDevices: (
  params: Parameters<typeof devicesResource.listFn>[0],
) => Promise<DeviceListResponse> = devicesResource.listFn;

/** Sort + filters → the list's wire params. The export sends the same, so the file comes out with
 *  the rows and order on screen, sorted or not (ADO 120974). */
export const devicesRequestQuery = devicesResource.toRequestQuery;
export const getDevice: (id: string) => Promise<Device> = devicesResource.get;
export const createDevice: (body: CreateDeviceBody) => Promise<Device> = devicesResource.create;
export const updateDevice: (id: string, body: CreateDeviceBody) => Promise<Device> =
  devicesResource.update;
export const setDeviceStatus: (id: string, status: DeviceStatus) => Promise<void> =
  devicesResource.setStatus;
export const deleteDevice: (id: string) => Promise<void> = devicesResource.remove;

/** Active categories for the Add/Edit Device form and the listing filter. Backend paginates by cursor; the
 *  drawer needs the full set, so request a high limit (open item: cursor paging).
 *
 *  `status: 'active'` is load-bearing and server-side: the response is `{ id, name }` with no
 *  `status` on it, so an inactive master Category can only be excluded by the param — filtering
 *  the parsed list is structurally impossible (ADO 113384, the exact trap that made 117569's
 *  first fix a no-op). The backend supports and e2e-tests it on `meta/categories`. */
export async function listMetaCategories(): Promise<MetaCategory[]> {
  const data = await apiClient.get(endpoints.products.metaCategories, {
    params: { limit: 100, status: 'active' },
  });
  return metaCategorySchema.array().parse(data);
}

/** Selectable components for the Applicable Components filter. Cursor-paginated;
 *  request a high limit for now (server-side `search` is a pending backend ask). Excludes
 *  inactive components via the backend's own `status` query param — the response items
 *  themselves never carry a `status` field (`ProductMetaItem = { id, name }`, confirmed
 *  against greenway_api), so a client-side filter on that field is always a no-op.
 *  Selecting an inactive component used to reach save and throw a raw "invalid" error
 *  from the backend instead of never being offered (ADO 117569). */
export async function listMetaComponents(): Promise<MetaComponent[]> {
  const data = await apiClient.get(endpoints.products.metaComponents, {
    params: { limit: 100, status: 'active' },
  });
  return metaComponentSchema.array().parse(data);
}

/** Presign for a device image or installation-manual upload. `attachmentType` is `'image'` for
 *  the image zone, `'manual'` for the installation guide; `ctx.durationSeconds` (videos) flows
 *  through to the request. Shared contract lives in `requestProductPresign`. */
export function getDevicePresignedUrl(
  file: File,
  attachmentType: 'image' | 'manual',
  ctx: PresignContext = {},
): Promise<PresignResult> {
  return requestProductPresign({
    file,
    entity: 'device',
    attachmentType,
    durationSeconds: ctx.durationSeconds,
  });
}
